import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, MapPin, Sparkles, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { type Listing, toggleSave, CURRENT_USER } from "@/data/listings";
import { cn } from "@/lib/utils";

interface ListingCardProps {
  listing: Listing;
  onSaveToggle?: (id: string, saved: boolean) => void;
}


const categoryColor: Record<string, string> = {
  wedding: "#f3e8ff",
  engagement: "#fce7f3",
  party: "#fef9c3",
  formal: "#e0f2fe",
  casual: "#f0fdf4",
};

const categoryTextColor: Record<string, string> = {
  wedding: "#7c3aed",
  engagement: "#be185d",
  party: "#a16207",
  formal: "#0369a1",
  casual: "#166534",
};

export function ListingCard({ listing, onSaveToggle }: ListingCardProps) {
  const [saved, setSaved] = useState(listing.savedBy.includes(CURRENT_USER.id));
  const [savingAnim, setSavingAnim] = useState(false);

  const handleSave = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSavingAnim(true);
    const nowSaved = await toggleSave(listing.id, CURRENT_USER.id);
    setSaved(nowSaved);
    setSavingAnim(false);
    onSaveToggle?.(listing.id, nowSaved);
  };

  const discount = Math.round(((listing.originalPrice - listing.sellingPrice) / listing.originalPrice) * 100);

  return (
    <Link to={`/listing/${listing.id}`} className="block group">
      <div className="rounded-2xl overflow-hidden bg-card border border-border/60 hover:border-border hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5">
        {/* Image */}
        <div className="relative aspect-[3/4] overflow-hidden bg-muted">
          <img
            src={listing.images[0]}
            alt={listing.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />

          {/* Top badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-1.5">
            {listing.enhanced && (
              <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur-sm text-white text-xs">
                <Sparkles size={10} />
                <span>AI Enhanced</span>
              </div>
            )}
            <div
              className="px-2 py-0.5 rounded-full text-xs font-medium capitalize"
              style={{
                background: categoryColor[listing.category],
                color: categoryTextColor[listing.category],
              }}
            >
              {listing.category}
            </div>
          </div>

          {/* Save button */}
          <button
            onClick={handleSave}
            className={cn(
              "absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center backdrop-blur-sm transition-all duration-200",
              saved ? "bg-rose-500 text-white" : "bg-white/80 text-muted-foreground hover:bg-white"
            )}
          >
            <Heart
              size={15}
              className={cn("transition-transform", savingAnim && "scale-125")}
              fill={saved ? "currentColor" : "none"}
            />
          </button>

          {/* Worn count */}
          {listing.wornCount > 1 && (
            <div className="absolute bottom-3 left-3 flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/90 text-xs text-foreground">
              <RefreshCw size={9} />
              <span>Worn {listing.wornCount}×</span>
            </div>
          )}

          {/* Discount badge */}
          {discount >= 30 && (
            <div className="absolute bottom-3 right-3 px-2 py-0.5 rounded-full bg-rose-500 text-white text-xs font-bold">
              -{discount}%
            </div>
          )}
        </div>

        {/* Info */}
        <div className="p-3">
          <h3 className="text-sm font-semibold text-foreground line-clamp-1 mb-1 group-hover:text-primary transition-colors">
            {listing.title}
          </h3>

          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-base font-bold" style={{ color: "var(--rose)" }}>
                {listing.sellingPrice} SAR
              </span>
              <span className="text-xs text-muted-foreground line-through">
                {listing.originalPrice}
              </span>
            </div>
            <Badge variant="outline" className="text-xs px-1.5 py-0 border-border/60">
              {listing.size}
            </Badge>
          </div>

          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <img src={listing.sellerAvatar} alt="" className="w-4 h-4 rounded-full object-cover" />
              <span>{listing.sellerName.split(" ")[0]}</span>
            </div>
            <div className="flex items-center gap-0.5">
              <MapPin size={10} />
              <span>{listing.distanceKm} km</span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
